/**
 * Normalises organisation / stage payloads for per-grade rows (grade + student strength).
 * Strength keys match {@link IStageCurriculumGradeRow} and camelCase UI mirrors.
 */

import { CURRICULUM_GRADE_FIELD_KEYS } from '../constants/client-creation.constants'
import type { IStageCurriculumGradeRow } from '../models/aggregated-lead.model'

/**
 * Wire keys for grade-row strength: {@link IStageCurriculumGradeRow} `student_strength`
 * and camelCase `studentStrength` on hydrated GET / UI payloads.
 */
const STUDENT_STRENGTH_WIRE_KEYS = ['student_strength', 'studentStrength'] as const

export function coerceFiniteGrade(value: unknown): number | null {
    if (value === undefined || value === null) return null
    const parsedGrade = Number(String(value).trim())
    return Number.isFinite(parsedGrade) ? parsedGrade : null
}

/** First non-empty string among known strength field names on a grade row object. */
export function extractGradeStrengthDisplayString(row: Record<string, unknown>): string {
    for (const fieldKey of STUDENT_STRENGTH_WIRE_KEYS) {
        const raw = row[fieldKey]
        if (raw !== undefined && raw !== null && String(raw).trim().length) {
            return String(raw)
        }
    }
    return ''
}

export function extractGradeNumberFromRow(row: Record<string, unknown>): number | null {
    for (const fieldKey of CURRICULUM_GRADE_FIELD_KEYS) {
        if (row[fieldKey] !== undefined && row[fieldKey] !== null) {
            const parsedGrade = coerceFiniteGrade(row[fieldKey])
            if (parsedGrade !== null) return parsedGrade
        }
    }
    return null
}

export interface IParsedCurriculumGradeRow {
    grade: number
    studentStrength: string
}

/**
 * Parse curriculum_detail.grades from GET organisation / aggregated payloads.
 * Supports: array of objects, array of grade scalars, or record of grade -> strength / row.
 */
export function parseCurriculumGradesUnknown(gradesRaw: unknown): IParsedCurriculumGradeRow[] {
    if (gradesRaw === undefined || gradesRaw === null) return []

    if (typeof gradesRaw === 'object' && !Array.isArray(gradesRaw)) {
        const entries = Object.entries(gradesRaw as Record<string, unknown>)
        if (!entries?.length) return []

        return entries
            .map(([gradeKey, rawEntry]): IParsedCurriculumGradeRow | null => {
                const grade = coerceFiniteGrade(gradeKey)
                if (grade === null) return null
                if (rawEntry !== null && typeof rawEntry === 'object' && !Array.isArray(rawEntry)) {
                    const str = extractGradeStrengthDisplayString(
                        rawEntry as Record<string, unknown>,
                    )
                    return { grade, studentStrength: str }
                }
                if (typeof rawEntry === 'number' || typeof rawEntry === 'string') {
                    const trimmed = String(rawEntry).trim()
                    return { grade, studentStrength: trimmed }
                }
                return { grade, studentStrength: '' }
            })
            .filter((parsedRow): parsedRow is IParsedCurriculumGradeRow => parsedRow !== null)
    }

    if (!Array.isArray(gradesRaw) || !gradesRaw?.length) return []

    return gradesRaw
        .map((rawGradeItem): IParsedCurriculumGradeRow | null => {
            if (typeof rawGradeItem === 'object' && rawGradeItem !== null) {
                const row = rawGradeItem as Record<string, unknown>
                const grade = extractGradeNumberFromRow(row)
                if (grade === null) return null
                return {
                    grade,
                    studentStrength: extractGradeStrengthDisplayString(row),
                }
            }
            const grade = coerceFiniteGrade(rawGradeItem)
            return grade !== null ? { grade, studentStrength: '' } : null
        })
        .filter((parsedRow): parsedRow is IParsedCurriculumGradeRow => parsedRow !== null)
}
